import { zodResolver } from '@hookform/resolvers/zod';
import { usePresentVoiceText, useVoiceCopy } from '@/platform/voice/use-voice';
import {
  AppModuleCode,
  APP_MODULE_CODES,
  buildCreateTicketFormSchema,
  DEFAULT_TICKET_CATALOG_SETTINGS,
  eurosToCents,
  ticketAttachmentSchema,
  TICKET_ATTACHMENT_MAX_BYTES,
  TICKET_ATTACHMENT_MAX_FILES,
  TICKET_CREATE_SUGGEST_DEBOUNCE_MS,
  IMPACT_LEVEL_LABELS,
  INCIDENT_SEVERITY_LABELS,
  CHANGE_RISK_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_TYPE_LABELS,
  URGENCY_LEVEL_LABELS,
  ChangeRisk,
  ImpactLevel,
  IncidentSeverity,
  TicketPriority,
  TicketType,
  isTicketTypeEnabled,
  fromChangeDatetimeLocalValue,
  parseAffectedServices,
  resolveIncidentPriority,
  ticketCreateSuggestQuery,
  type CreateTicketFormValues,
} from '@gb-ticket/shared-types';

const AUTO_CATEGORY_VALUE = '__auto__';
import { Loader2Icon, MonitorSmartphoneIcon, PaperclipIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FilePickerButton } from '@/components/file-picker-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import { leafCategories } from '@/products/helpdesk/features/tickets/category-options';
import { RichTextEditor } from '@/products/helpdesk/features/tickets/rich-text-editor';
import { TicketCreateSuggestions } from '@/products/helpdesk/features/tickets/ticket-create-suggestions';
import {
  useCreateTicket,
  useTicket,
  useTicketLookups,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { clearChatTicketDraft, readChatTicketDraft } from '@/products/helpdesk/features/ai/use-ai';
import { changeCalendarPathForInstant } from '@/products/helpdesk/features/changes/change-upcoming';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError, apiFetch } from '@/lib/api';
import { formatBytes } from '@/lib/format';

function parseTicketTypeParam(raw: string | null): TicketType | null {
  if (!raw) {
    return null;
  }
  const match = Object.values(TicketType).find((code) => code === raw);
  return match ?? null;
}

export function TicketCreateForm() {
  const copy = useVoiceCopy();
  const present = usePresentVoiceText();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const paths = useAppPaths();
  const me = useMe();
  const knowledgeOn = useModule(AppModuleCode.KNOWLEDGE);
  const remoteOn = useModule(AppModuleCode.REMOTE_SUPPORT);
  const lookups = useTicketLookups();
  const createTicket = useCreateTicket();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const chatDraft = params.get('aus') === 'chat' ? readChatTicketDraft() : null;
  const followUpRef = params.get('folgeVon');
  const followUpSource = useTicket(followUpRef ?? undefined);
  const requestedType = parseTicketTypeParam(params.get('typ'));
  const catalogSettings = lookups.data?.catalogSettings ?? DEFAULT_TICKET_CATALOG_SETTINGS;
  const showCategoryPicker = !paths.isPortal || catalogSettings.portalCategoryPickerEnabled;
  const showTypePicker = !paths.isPortal || catalogSettings.portalTypePickerEnabled;
  /** Portal with picker on: user must choose. Otherwise empty → keyword auto-assign. */
  const categoryRequired = paths.isPortal && catalogSettings.portalCategoryPickerEnabled;
  const formSchema = useMemo(
    () => buildCreateTicketFormSchema({ categoryRequired }),
    [categoryRequired],
  );

  const form = useForm<CreateTicketFormValues>({
    resolver: (values, context, options) => zodResolver(formSchema)(values, context, options),
    defaultValues: {
      type: requestedType ?? TicketType.SERVICE_REQUEST,
      subject: chatDraft?.subject ?? params.get('betreff') ?? '',
      description: chatDraft?.description ?? params.get('beschreibung') ?? '',
      categoryId: '',
      priority: TicketPriority.MEDIUM,
      estimatedValue: '',
      impact: ImpactLevel.MEDIUM,
      urgency: ImpactLevel.MEDIUM,
      severity: IncidentSeverity.MINOR,
      changeRisk: ChangeRisk.MEDIUM,
      rollbackPlan: '',
      scheduledStart: '',
      scheduledEnd: '',
      affectedServices: '',
    },
  });

  const leaves = leafCategories(lookups.data?.categories ?? []);
  const groups = [...new Set(leaves.map((item) => item.group).filter(Boolean))] as string[];
  const subject = useWatch({ control: form.control, name: 'subject' });
  const description = useWatch({ control: form.control, name: 'description' });
  const categoryId = useWatch({ control: form.control, name: 'categoryId' });
  const ticketType = useWatch({ control: form.control, name: 'type' });
  const impact = useWatch({ control: form.control, name: 'impact' });
  const urgency = useWatch({ control: form.control, name: 'urgency' });
  const severity = useWatch({ control: form.control, name: 'severity' });
  const isIncident = ticketType === TicketType.INCIDENT;
  const isChange = ticketType === TicketType.CHANGE && !paths.isPortal;
  usePageTitle(paths.isPortal ? 'Neue Anfrage' : isChange ? 'Neue Änderung' : 'Neues Ticket');
  const [suggestQuery, setSuggestQuery] = useState<string | null>(null);
  const availableTypes = useMemo(
    () =>
      Object.values(TicketType).filter((code) => {
        if (paths.isPortal && code !== TicketType.SERVICE_REQUEST && code !== TicketType.INCIDENT) {
          return false;
        }
        return isTicketTypeEnabled(code, me.data?.enabledModules ?? APP_MODULE_CODES);
      }),
    [me.data?.enabledModules, paths.isPortal],
  );

  useEffect(() => {
    if (!showCategoryPicker && form.getValues('categoryId')) {
      form.setValue('categoryId', '');
      form.clearErrors('categoryId');
    }
  }, [form, showCategoryPicker]);

  useEffect(() => {
    if (!availableTypes.includes(ticketType)) {
      form.setValue('type', TicketType.SERVICE_REQUEST);
    }
  }, [availableTypes, form, ticketType]);

  useEffect(() => {
    if (!isIncident) {
      return;
    }
    const next = resolveIncidentPriority({
      impact: impact ?? ImpactLevel.MEDIUM,
      urgency: urgency ?? ImpactLevel.MEDIUM,
      severity: paths.isPortal ? IncidentSeverity.MINOR : (severity ?? IncidentSeverity.MINOR),
    });
    form.setValue('priority', next.priority);
  }, [form, impact, isIncident, paths.isPortal, severity, urgency]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSuggestQuery(ticketCreateSuggestQuery(subject, description));
    }, TICKET_CREATE_SUGGEST_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [subject, description]);

  useEffect(() => {
    const source = followUpSource.data;
    if (!source || params.get('betreff') || chatDraft) {
      return;
    }
    if (form.getValues('subject')) {
      return;
    }
    form.setValue('subject', `Zu Ticket #${source.number}: ${source.subject}`);
  }, [chatDraft, followUpSource.data, form, params]);

  function onPickFiles(list: FileList | null) {
    if (!list) {
      return;
    }
    const next = [...files, ...Array.from(list)];
    if (next.length > TICKET_ATTACHMENT_MAX_FILES) {
      setFileError(`Es sind höchstens ${TICKET_ATTACHMENT_MAX_FILES} Dateien erlaubt.`);
      return;
    }
    const oversized = next.find((file) => file.size > TICKET_ATTACHMENT_MAX_BYTES);
    if (oversized) {
      setFileError(`„${oversized.name}“ ist größer als 10 MB.`);
      return;
    }
    setFileError(null);
    setFiles(next);
  }

  async function onSubmit(values: CreateTicketFormValues) {
    try {
      const explicitCategoryId =
        typeof values.categoryId === 'string' && values.categoryId.length > 0
          ? values.categoryId
          : undefined;
      const ticketTypeForApi = showTypePicker ? values.type : undefined;
      const resolvedType = ticketTypeForApi ?? values.type;
      const ticket = await createTicket.mutateAsync({
        ...(ticketTypeForApi ? { type: ticketTypeForApi } : {}),
        subject: values.subject,
        description: values.description,
        ...(explicitCategoryId ? { categoryId: explicitCategoryId } : {}),
        priority: values.priority,
        estimatedValueCents: eurosToCents(values.estimatedValue),
        ...(followUpSource.data ? { followUpOfTicketId: followUpSource.data.id } : {}),
        ...(resolvedType === TicketType.INCIDENT
          ? {
              impact: values.impact,
              urgency: values.urgency,
              severity: paths.isPortal ? IncidentSeverity.MINOR : values.severity,
            }
          : {}),
        ...(resolvedType === TicketType.CHANGE
          ? {
              risk: values.changeRisk,
              rollbackPlan: values.rollbackPlan,
              scheduledStart: fromChangeDatetimeLocalValue(values.scheduledStart ?? ''),
              scheduledEnd: fromChangeDatetimeLocalValue(values.scheduledEnd ?? ''),
              affectedServices: parseAffectedServices(values.affectedServices ?? ''),
            }
          : {}),
      });
      if (files.length > 0) {
        const ref = String(ticket.number);
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          await apiFetch(`/tickets/${ref}/attachments`, ticketAttachmentSchema, {
            method: 'POST',
            body: formData,
          });
        }
      }
      if (values.type === TicketType.CHANGE && !paths.isPortal) {
        const startIso = fromChangeDatetimeLocalValue(values.scheduledStart ?? '');
        const endIso = fromChangeDatetimeLocalValue(values.scheduledEnd ?? '');
        const changeHref = `${paths.ticket(ticket.number)}#change`;
        const calendarHref = startIso && endIso ? changeCalendarPathForInstant(startIso) : null;
        toast.success(
          <span>
            Änderung #{ticket.number} wurde angelegt.{' '}
            <a href={changeHref} className="font-medium underline">
              {calendarHref ? 'Zum Change' : 'Zeitfenster setzen'}
            </a>
            {calendarHref ? (
              <>
                {' · '}
                <a href={calendarHref} className="font-medium underline">
                  Im Kalender
                </a>
              </>
            ) : null}
          </span>,
        );
        if (!calendarHref) {
          toast.warning('Ohne Zeitfenster erscheint der Change nicht im Kalender.');
        }
        for (const warning of ticket.changeWarnings ?? []) {
          if (warning.severity === 'strong') {
            toast.error(warning.message);
          } else {
            toast.warning(warning.message);
          }
        }
      } else {
        toast.success(
          paths.isPortal
            ? copy('helpdesk.ticket.created.portal', { number: String(ticket.number) })
            : `Ticket #${ticket.number} wurde angelegt.`,
        );
      }
      clearChatTicketDraft();
      void navigate(
        paths.ticket(ticket.number) + (values.type === TicketType.CHANGE ? '#change' : ''),
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : files.length > 0
            ? 'Ticket angelegt, aber ein Anhang konnte nicht hochgeladen werden.'
            : 'Das Ticket konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <section className="mx-auto max-w-6xl space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
          <Link to={paths.tickets}>
            {paths.isPortal ? '← Zurück zu meinen Anfragen' : '← Zurück zur Übersicht'}
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {paths.isPortal ? 'Neue Anfrage an die IT' : isChange ? 'Neue Änderung' : 'Neues Ticket'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {followUpSource.data
            ? `Dieses Ticket setzt Vorgang #${followUpSource.data.number} fort, weil die Wiedereröffnungsfrist abgelaufen ist.`
            : paths.isPortal
              ? copy('helpdesk.ticket.create.portal-lead')
              : isChange
                ? copy('helpdesk.ticket.create.change-lead')
                : copy('helpdesk.ticket.create.agent-lead')}
        </p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <form className="space-y-6" onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
          <Card>
            <CardHeader>
              <CardTitle>
                {paths.isPortal ? 'Worum geht es?' : isChange ? 'Änderung' : 'Angaben'}
              </CardTitle>
              <CardDescription>
                {paths.isPortal
                  ? 'Ein kurzer Betreff und ein paar Sätze reichen. Je klarer, desto schneller können wir helfen.'
                  : isChange
                    ? 'Art ist auf Änderung gesetzt. Zusätzlich erscheinen Risiko, Fenster und Rollback.'
                    : 'Betreff, Kategorie und Priorität helfen bei der Zuordnung.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="subject">Betreff</Label>
                <Input
                  id="subject"
                  autoComplete="off"
                  {...form.register('subject')}
                  aria-invalid={Boolean(form.formState.errors.subject)}
                />
                {form.formState.errors.subject && (
                  <p className="text-sm text-destructive">
                    {present(form.formState.errors.subject.message)}
                  </p>
                )}
              </div>
              {showTypePicker ? (
                <div className="space-y-1.5">
                  <Label htmlFor="type">Art</Label>
                  <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="type" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTypes.map((code) => (
                            <SelectItem key={code} value={code}>
                              {TICKET_TYPE_LABELS[code]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              ) : (
                <div className="space-y-1.5 sm:col-span-2">
                  <p className="text-xs text-muted-foreground">
                    Die Art wird automatisch anhand von Stichwörtern und dem Portal-Standard
                    zugeordnet.
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="priority">Priorität</Label>
                <Controller
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isIncident}
                    >
                      <SelectTrigger id="priority" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(TicketPriority).map((code) => (
                          <SelectItem key={code} value={code}>
                            {TICKET_PRIORITY_LABELS[code]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {isIncident && (
                  <p className="text-xs text-muted-foreground">
                    Wird aus Impact und Urgency ermittelt
                    {paths.isPortal ? '' : '; Major hebt auf mindestens Hoch.'}
                  </p>
                )}
              </div>
              {isIncident && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="impact">Impact</Label>
                    <Controller
                      control={form.control}
                      name="impact"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="impact" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ImpactLevel).map((code) => (
                              <SelectItem key={code} value={code}>
                                {IMPACT_LEVEL_LABELS[code]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="urgency">Urgency</Label>
                    <Controller
                      control={form.control}
                      name="urgency"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="urgency" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ImpactLevel).map((code) => (
                              <SelectItem key={code} value={code}>
                                {URGENCY_LEVEL_LABELS[code]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  {!paths.isPortal && (
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="severity">Schweregrad</Label>
                      <Controller
                        control={form.control}
                        name="severity"
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger id="severity" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(IncidentSeverity).map((code) => (
                                <SelectItem key={code} value={code}>
                                  {INCIDENT_SEVERITY_LABELS[code]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        Major kennzeichnet den Leit-Incident, unter dem weitere Tickets gebündelt
                        werden können.
                      </p>
                    </div>
                  )}
                </>
              )}
              {isChange && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="changeRisk">Risiko</Label>
                    <Controller
                      control={form.control}
                      name="changeRisk"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="changeRisk" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ChangeRisk).map((code) => (
                              <SelectItem key={code} value={code}>
                                {CHANGE_RISK_LABELS[code]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="scheduledStart">Geplanter Start</Label>
                    <Input
                      id="scheduledStart"
                      type="datetime-local"
                      {...form.register('scheduledStart')}
                    />
                    {form.formState.errors.scheduledStart && (
                      <p className="text-sm text-destructive">
                        {present(form.formState.errors.scheduledStart.message)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="scheduledEnd">Geplantes Ende</Label>
                    <Input
                      id="scheduledEnd"
                      type="datetime-local"
                      {...form.register('scheduledEnd')}
                    />
                    {form.formState.errors.scheduledEnd && (
                      <p className="text-sm text-destructive">
                        {present(form.formState.errors.scheduledEnd.message)}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    Zeiten: Europe/Berlin.
                  </p>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="rollbackPlan">Rollback-Plan</Label>
                    <textarea
                      id="rollbackPlan"
                      rows={3}
                      className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
                      {...form.register('rollbackPlan')}
                      placeholder="Wie die Änderung rückgängig gemacht wird …"
                    />
                    {form.formState.errors.rollbackPlan && (
                      <p className="text-sm text-destructive">
                        {present(form.formState.errors.rollbackPlan.message)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="affectedServices">Betroffene Services</Label>
                    <textarea
                      id="affectedServices"
                      rows={2}
                      className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
                      {...form.register('affectedServices')}
                      placeholder="Ein Service pro Zeile, z. B. Exchange"
                    />
                    <p className="text-xs text-muted-foreground">
                      {copy('helpdesk.ticket.create.change-assets')}
                    </p>
                  </div>
                </>
              )}
              {showCategoryPicker ? (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="categoryId">Kategorie</Label>
                  <Controller
                    control={form.control}
                    name="categoryId"
                    render={({ field }) => (
                      <Select
                        value={
                          field.value
                            ? field.value
                            : categoryRequired
                              ? undefined
                              : AUTO_CATEGORY_VALUE
                        }
                        onValueChange={(next) =>
                          field.onChange(next === AUTO_CATEGORY_VALUE ? '' : next)
                        }
                      >
                        <SelectTrigger
                          id="categoryId"
                          className="w-full"
                          aria-invalid={Boolean(form.formState.errors.categoryId)}
                        >
                          <SelectValue
                            placeholder={
                              categoryRequired ? 'Bitte wählen' : 'Automatisch (Stichwörter)'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {!categoryRequired ? (
                            <SelectItem value={AUTO_CATEGORY_VALUE}>
                              Automatisch (Stichwörter)
                            </SelectItem>
                          ) : null}
                          {groups.map((group) => (
                            <SelectGroup key={group}>
                              <SelectLabel>{group}</SelectLabel>
                              {leaves
                                .filter((item) => item.group === group)
                                .map((item) => (
                                  <SelectItem key={item.id} value={item.id}>
                                    {item.name}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          ))}
                          {leaves
                            .filter((item) => item.group === null)
                            .map((item) => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {!categoryRequired ? (
                    <p className="text-xs text-muted-foreground">
                      Ohne Auswahl wird die Kategorie aus Stichwörtern in Betreff und Beschreibung
                      ermittelt.
                    </p>
                  ) : null}
                  {form.formState.errors.categoryId && (
                    <p className="text-sm text-destructive">
                      {present(form.formState.errors.categoryId.message)}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5 sm:col-span-2">
                  <p className="text-xs text-muted-foreground">
                    Die Kategorie wird automatisch anhand von Stichwörtern in Betreff und
                    Beschreibung zugeordnet.
                  </p>
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="estimatedValue">Geschätzter Wert (€, optional)</Label>
                <Input
                  id="estimatedValue"
                  inputMode="decimal"
                  placeholder="z. B. 500,00"
                  {...form.register('estimatedValue')}
                  aria-invalid={Boolean(form.formState.errors.estimatedValue)}
                />
                <p className="text-xs text-muted-foreground">
                  {paths.isPortal
                    ? 'Nur nötig, wenn es um etwas Kostenpflichtiges geht — z. B. eine Lizenz oder ein Gerät. Sonst leer lassen.'
                    : 'Wird für Freigaben ab einem Schwellenwert verwendet, z. B. bei kostenpflichtigen Anfragen.'}
                </p>
                {form.formState.errors.estimatedValue && (
                  <p className="text-sm text-destructive">
                    {present(form.formState.errors.estimatedValue.message)}
                  </p>
                )}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Controller
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <RichTextEditor
                      id="description"
                      value={field.value}
                      onChange={field.onChange}
                      invalid={Boolean(form.formState.errors.description)}
                    />
                  )}
                />
                {form.formState.errors.description && (
                  <p className="text-sm text-destructive">
                    {present(form.formState.errors.description.message)}
                  </p>
                )}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="attachments">Anhänge</Label>
                <FilePickerButton
                  id="attachments"
                  multiple
                  onPick={onPickFiles}
                  chosenLabel={
                    files.length === 0
                      ? 'Keine Datei ausgewählt'
                      : files.length === 1
                        ? (files[0]?.name ?? '1 Datei ausgewählt')
                        : `${files.length} Dateien ausgewählt`
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Optional, höchstens {TICKET_ATTACHMENT_MAX_FILES} Dateien à 10 MB (PDF, Bilder,
                  Office, ZIP, Text).
                </p>
                {fileError && <p className="text-sm text-destructive">{fileError}</p>}
                {files.length > 0 && (
                  <ul className="space-y-1 text-sm">
                    {files.map((file) => (
                      <li
                        key={`${file.name}-${file.size}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <PaperclipIcon className="size-4 shrink-0" aria-hidden />
                          <span className="truncate">{file.name}</span>
                          <span className="text-muted-foreground">{formatBytes(file.size)}</span>
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`${file.name} entfernen`}
                          onClick={() =>
                            setFiles((current) => current.filter((item) => item !== file))
                          }
                        >
                          <XIcon />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
          <div className="flex gap-2">
            <Button type="submit" disabled={createTicket.isPending || lookups.isPending}>
              {createTicket.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              {paths.isPortal ? 'Anfrage senden' : 'Ticket anlegen'}
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link to={paths.tickets}>Abbrechen</Link>
            </Button>
          </div>
        </form>
        <div className="space-y-6">
          {remoteOn && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MonitorSmartphoneIcon className="size-4" aria-hidden />
                  Fernwartung
                </CardTitle>
                <CardDescription>
                  Drei verschiedene Werte, die nicht ins Formular gehören: der Kopplungscode (nur
                  Connector ↔ Mitarbeiterportal, nie in RustDesk), die Geräte-GUID (dieser PC im
                  Ticket-System) und die RustDesk-ID (kommt von RustDesk, nicht aus dem Portal).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>
                  1. Die IT installiert auf dem PC <strong>RustDesk</strong> (Program Files) und den{' '}
                  <strong>Mitarbeiterportal Connector</strong> (kleines Programm in der Taskleiste).
                  Danach unter{' '}
                  <Link
                    to={paths.settings}
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Einstellungen
                  </Link>{' '}
                  einmal mit einem Kopplungscode koppeln (Code nie in RustDesk).
                </p>
                <p>2. Diese Anfrage absenden und das Ticket öffnen.</p>
                <p>{copy('helpdesk.ticket.create.remote-step')}</p>
              </CardContent>
            </Card>
          )}
          {knowledgeOn && (
            <TicketCreateSuggestions
              q={suggestQuery}
              categoryId={categoryId && categoryId.length > 10 ? categoryId : undefined}
            />
          )}
        </div>
      </div>
    </section>
  );
}
